import { Component, ElementRef, EventEmitter, Output } from '@angular/core';
import { AUTHORITIES, AuthorityRecord } from './dateless-authorities';

/* =============================================================================
   pla-dateless-authorities — the Historic Issuing Authorities archive
   (build pack §6.5). A to Z selector driving a two-column detail panel with
   three card states: ready (archive frame + caption + credit + source link),
   gap (line-art veteran car + "Photograph still being sought" + contribute),
   never-issued (I, Q). Only rights-checked content renders; captions and
   credits come from the content file verbatim.

   Accessibility: the letter grid is a roving-tabindex listbox; arrow keys
   move, Home/End jump, Enter/Space select; aria-selected on the active
   letter; the detail panel is aria-live so selection is announced.
   ========================================================================== */

@Component({
  selector: 'pla-dateless-authorities',
  templateUrl: './dateless-authorities.component.html',
  styleUrls: ['./dateless-authorities.component.scss'],
})
export class DatelessAuthoritiesComponent {
  public records: AuthorityRecord[] = AUTHORITIES;
  public selected: AuthorityRecord = AUTHORITIES[0];

  /** Surfaces contribute-a-photograph intent to the page (flow unbuilt). */
  @Output() contribute = new EventEmitter<string>();

  constructor(private el: ElementRef<HTMLElement>) {}

  public select(rec: AuthorityRecord): void {
    this.selected = rec;
  }

  public isSelected(rec: AuthorityRecord): boolean {
    return this.selected.letter === rec.letter;
  }

  public trackByLetter(_i: number, rec: AuthorityRecord): string {
    return rec.letter;
  }

  /** "1903 to 1912" | "from 1903" (never-issued rows render no period). */
  public period(rec: AuthorityRecord): string {
    return rec.issueEnd ? `1903 to ${rec.issueEnd}` : 'from 1903';
  }

  public creditLine(rec: AuthorityRecord): string {
    if (!rec.credit) return '';
    const prefix = rec.licence ? 'Source: ' : 'Courtesy ';
    const licence = rec.licence ? `, ${rec.licence}` : '';
    // CC BY and CC BY-SA require a derivative to say it has been changed.
    const changed = rec.cropped ? ', cropped' : '';
    return `${prefix}${rec.credit}${licence}${changed}`;
  }

  public onKeydown(ev: KeyboardEvent, index: number): void {
    const keys: { [k: string]: number } = {
      ArrowRight: index + 1, ArrowDown: index + 1,
      ArrowLeft: index - 1, ArrowUp: index - 1,
      Home: 0, End: this.records.length - 1,
    };
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this.select(this.records[index]);
      return;
    }
    if (!(ev.key in keys)) return;
    ev.preventDefault();
    const next = Math.max(0, Math.min(this.records.length - 1, keys[ev.key]));
    const tiles = this.el.nativeElement.querySelectorAll<HTMLElement>('.al');
    if (tiles[next]) {
      tiles[next].focus();
      this.select(this.records[next]);
    }
  }

  public onContribute(): void {
    this.contribute.emit(this.selected.letter);
  }
}
